import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { defaultUniqueIdentifier } from 'app/constants'
import { SortConfig } from 'core/helpers/createSorter'
import { IDataKeys } from 'k8s/datakeys.model'
import { __, allPass, dissocPath, equals, map, mergeLeft, path, pathEq, pipe, split } from 'ramda'
import { adjustWith, arrayIfNil, emptyArr, ensureArray, pathStr } from 'utils/fp'

export const paramsStoreKey = 'cachedParams'
export const dataStoreKey = 'cachedData'
export const loadingStoreKey = 'loadingData'
export const updatingStoreKey = 'updatingData'
export const backgroundRefreshStoreKey = 'refreshing'
export const lastRefreshTimeStoreKey = 'lastRefreshTime'

export type DataType = IDataKeys[keyof IDataKeys]
export type ParamsType = Record<string, unknown>
export type SortableParamsType = ParamsType & SortConfig
type Optional<T> = T extends null ? void : T

/**
 * Normalized entity state - stores items as both an ordered ID array and an entity map
 * This provides O(1) lookup by ID while maintaining order
 */
export interface NormalizedEntityState<T = any> {
  ids: Array<string | number>
  entities: Record<string | number, T>
}

// Generate the ID the same way it was stored (composite key for multiple identifiers)
const matchUniqueIdentifiers = (item: any, uniqueIdentifierStrPaths: string[]): string =>
  map(pathStr(__, item), uniqueIdentifierStrPaths).join('-')

/**
 * Convert array to normalized format for backward compatibility
 * Also handles already-normalized state
 */
const normalizeIfNeeded = <T>(
  data: any,
  uniqueIdentifier: string | string[] = 'id',
): NormalizedEntityState<T> => {
  if (!data) {
    return { ids: [], entities: {} }
  }
  // Already normalized
  if (Array.isArray(data.ids) && typeof data.entities === 'object') {
    return data
  }
  // Is an array - convert to normalized
  if (Array.isArray(data)) {
    // Use index as fallback if no id field exists
    const ids: Array<string | number> = []
    const entities: Record<string | number, T> = {}
    data.forEach((item, index) => {
      const id =
        (typeof uniqueIdentifier === 'string'
          ? pathStr(uniqueIdentifier, item)
          : matchUniqueIdentifiers(item, ensureArray(uniqueIdentifier))) ?? index
      ids.push(id)
      entities[id] = item
    })
    return { ids, entities }
  }
  // Single item or object - convert to normalized
  return { ids: [], entities: {} }
}

/**
 * Convert normalized state back to array for backward compatibility
 */
const denormalizeToArray = <T>(normalized: NormalizedEntityState<T> | any): T[] => {
  if (!normalized) return emptyArr
  // Already an array
  if (Array.isArray(normalized)) return normalized
  // Is normalized format
  if (normalized.ids && normalized.entities) {
    return normalized.ids.map((id) => normalized.entities[id]).filter(Boolean)
  }
  return emptyArr
}

export interface CacheState {
  [dataStoreKey]: Record<string, NormalizedEntityState | any>
  [paramsStoreKey]: Record<string, ParamsType[]>
  [loadingStoreKey]: Record<string, boolean>
  [updatingStoreKey]: Record<string, boolean>
  [backgroundRefreshStoreKey]: Record<string, boolean>
  [lastRefreshTimeStoreKey]: Record<string, number>
}

export const initialState: CacheState = {
  [dataStoreKey]: {},
  [paramsStoreKey]: {},
  [loadingStoreKey]: {},
  [updatingStoreKey]: {},
  [backgroundRefreshStoreKey]: {},
  [lastRefreshTimeStoreKey]: {},
}

const getIdentifiersMatcher = (uniqueIdentifier: string | string[], params: ParamsType) => {
  const uniqueIdentifierPaths = uniqueIdentifier
    ? ensureArray(uniqueIdentifier).map(split('.'))
    : emptyArr
  const matchIdentifier = (idPath) => pathEq(idPath, path(idPath, params))
  return allPass(map(matchIdentifier, uniqueIdentifierPaths))
}

const reducers = {
  setLoading: <D extends keyof IDataKeys>(
    state,
    {
      payload: { cacheKey, loading },
    }: PayloadAction<{
      cacheKey: D
      loading: boolean
    }>,
  ) => {
    state[loadingStoreKey][cacheKey] = loading
  },
  setBackgroundRefresh: <D extends keyof IDataKeys>(
    state,
    {
      payload: { cacheKey, refreshing },
    }: PayloadAction<{
      cacheKey: D
      refreshing: boolean
    }>,
  ) => {
    state[backgroundRefreshStoreKey][cacheKey] = refreshing
  },
  setLastRefreshTime: <D extends keyof IDataKeys>(
    state,
    {
      payload: { cacheKey, lastRefreshTime },
    }: PayloadAction<{
      cacheKey: D
      lastRefreshTime: number
    }>,
  ) => {
    state[lastRefreshTimeStoreKey][cacheKey] = lastRefreshTime
  },
  setUpdating: <D extends keyof IDataKeys>(
    state,
    {
      payload: { cacheKey, updating },
    }: PayloadAction<{
      cacheKey: D
      updating: boolean
    }>,
  ) => {
    state[updatingStoreKey][cacheKey] = updating
  },
  addItem: <D extends keyof IDataKeys, T extends Record<string, unknown> = Record<string, unknown>>(
    state,
    {
      payload: { cacheKey, params, item, uniqueIdentifier = defaultUniqueIdentifier },
    }: PayloadAction<{
      cacheKey: D
      params: ParamsType
      item: T
      uniqueIdentifier?: string | string[]
    }>,
  ) => {
    const currentData = state[dataStoreKey][cacheKey]
    // Prefer item properties over params
    const mergedItem = mergeLeft(item, params) as any

    // Convert to normalized format if needed
    const normalized = normalizeIfNeeded<T>(currentData, uniqueIdentifier)

    // Get ID from item, default to length as temporary ID
    const id = mergedItem?.id ?? normalized.ids.length

    // Check if item with this ID already exists under a different key
    // This handles cases where an item was updated (e.g. ID changed) but the key remained the same
    const existingKey = normalized.ids.find((key) => {
      const entity = normalized.entities[key]
      return entity && entity.id === id
    })

    const keyToUse = existingKey !== undefined ? existingKey : id

    // Add to normalized state (only mutate, don't return)
    if (!normalized.entities[keyToUse]) {
      normalized.ids.push(keyToUse)
    }
    normalized.entities[keyToUse] = mergedItem

    // Update state with normalized data
    state[dataStoreKey][cacheKey] = normalized
  },
  updateItem: <
    D extends keyof IDataKeys,
    T extends Record<string, unknown> = Record<string, unknown>
  >(
    state,
    {
      payload: { uniqueIdentifier = defaultUniqueIdentifier, cacheKey, params, item },
    }: PayloadAction<{
      uniqueIdentifier?: string | string[]
      params: ParamsType
      cacheKey: D
      item: T
    }>,
  ) => {
    const currentData = state[dataStoreKey][cacheKey]
    const uniqueIdentifierStrPaths = ensureArray(uniqueIdentifier)

    // Create match predicate from params using uniqueIdentifier paths
    const matchIdentifier = (idPath) => {
      const pathArr = typeof idPath === 'string' ? split('.', idPath) : idPath
      return pathEq(pathArr, path(pathArr, params))
    }
    const matchPredicate = allPass(map(matchIdentifier, uniqueIdentifierStrPaths))

    // If it's normalized, update directly (O(1) performance)
    if (currentData && currentData.ids && currentData.entities) {
      // Merge params and item together
      // Prefer item properties over params
      const mergedItem = mergeLeft(item, params) as any

      // Find and update matching entity (only mutate, don't return)
      for (const id of currentData.ids) {
        const entity = currentData.entities[id]
        if (entity && matchPredicate(entity)) {
          // Directly mutate the entity in place (Immer is tracking this)
          Object.assign(currentData.entities[id], mergedItem)
          return // Don't return a value - just mutate
        }
      }
      return // No match found - nothing to update
    }

    // If it's an array or null, use the original logic (creates normalized state)
    // @ts-ignore
    state[dataStoreKey][cacheKey] = pipe(
      arrayIfNil,
      adjustWith(matchPredicate, mergeLeft(params, item)),
    )(currentData)
  },
  removeItem: <D extends keyof IDataKeys>(
    state,
    {
      payload: { uniqueIdentifier, cacheKey, params },
    }: PayloadAction<{ uniqueIdentifier: string | string[]; params: ParamsType; cacheKey: D }>,
  ) => {
    const currentData = state[dataStoreKey][cacheKey]
    const uniqueIdentifierStrPaths = ensureArray(uniqueIdentifier)

    const itemId = matchUniqueIdentifiers(params, uniqueIdentifierStrPaths) as string | number

    // Convert to normalized format if needed
    const normalized = normalizeIfNeeded(currentData, uniqueIdentifier)

    // Try to find and remove the item
    if (normalized.entities[itemId]) {
      delete normalized.entities[itemId]
      normalized.ids = normalized.ids.filter((id) => id !== itemId)
    } else {
      // If not found with composite ID, search by the first identifier value
      // This handles cases where items were stored with just the primary ID
      const primaryIdPath = uniqueIdentifierStrPaths[0]
      if (primaryIdPath) {
        const pathArray = Array.isArray(primaryIdPath) ? primaryIdPath : primaryIdPath.split('.')
        const primaryId = path(pathArray, params) as string | number
        if (primaryId && normalized.entities[primaryId]) {
          delete normalized.entities[primaryId]
          normalized.ids = normalized.ids.filter((id) => id !== primaryId)
        }
      }
    }

    // Update state with normalized data
    state[dataStoreKey][cacheKey] = normalized
  },
  upsertAll: <
    D extends keyof IDataKeys,
    T extends Record<string, unknown> = Record<string, unknown>
  >(
    state,
    {
      payload: { uniqueIdentifier = defaultUniqueIdentifier, cacheKey, params, items },
    }: PayloadAction<{
      uniqueIdentifier?: string | string[]
      cacheKey: D
      params?: ParamsType
      items: T[]
    }>,
  ) => {
    const currentData = state[dataStoreKey][cacheKey]
    const uniqueIdentifierStrPaths = uniqueIdentifier
      ? ensureArray(uniqueIdentifier)
      : (emptyArr as string[])

    // Convert to normalized format if needed
    const normalized = normalizeIfNeeded<T>(currentData, uniqueIdentifier)
    const mergedItems = ensureArray(items).map((item) => mergeLeft(params || {}, item))

    // Upsert items into normalized state (only mutate, don't return)
    mergedItems.forEach((item: any) => {
      const key = matchUniqueIdentifiers(item, uniqueIdentifierStrPaths)
      if (!normalized.entities[key]) {
        normalized.ids.push(key)
      }
      normalized.entities[key] = item
    })

    // Update data in state
    state[dataStoreKey][cacheKey] = normalized

    // If params are provided, add them to cachedParams so that
    // we know this query has already been resolved
    if (params) {
      const currentParams = state[paramsStoreKey][cacheKey] || []
      const paramExists = currentParams.some((item) => equals(item, params))
      if (!paramExists) {
        currentParams.push(params)
        state[paramsStoreKey][cacheKey] = currentParams
      }
    }
  },
  replaceAll: <D extends keyof IDataKeys, T>(
    state,
    {
      payload: { cacheKey, params, items, uniqueIdentifier = defaultUniqueIdentifier },
    }: PayloadAction<{
      cacheKey: D
      params?: ParamsType
      items: T[]
      uniqueIdentifier?: string | string[]
    }>,
  ) => {
    // Convert items array to normalized format
    const normalized = normalizeIfNeeded<T>(items, uniqueIdentifier)

    // Update data in state (only mutate, don't return)
    state[dataStoreKey][cacheKey] = normalized

    // If params are provided, replace the cached params array with the new params
    if (params) {
      state[paramsStoreKey][cacheKey] = [params]
    }
  },
  clearCache: <D extends keyof IDataKeys>(
    state,
    action?: PayloadAction<Optional<{ cacheKey: D }>>,
  ) => {
    const cacheKey = action?.payload?.cacheKey
    return cacheKey
      ? pipe<CacheState, CacheState, CacheState, CacheState>(
          dissocPath([dataStoreKey, cacheKey]),
          dissocPath([paramsStoreKey, cacheKey]),
          dissocPath([loadingStoreKey, cacheKey]),
        )(state)
      : initialState
  },
}

const { name: cacheStoreKey, reducer: cacheReducers, actions: cacheActions } = createSlice({
  name: 'cache',
  initialState,
  reducers,
})

export { cacheActions, cacheStoreKey, denormalizeToArray, normalizeIfNeeded }
export default cacheReducers
